import { memo, forwardRef } from "react"
import { Mail, LucideIcon, Shield } from "lucide-react"
import { cva } from "class-variance-authority"

import { cn } from "@f2e/ui/lib/utils"

import { ClientOAuthProvider } from "@/types"

const providerIcons: Record<ClientOAuthProvider, LucideIcon> = {
  [ClientOAuthProvider.Google]: Mail,
}

const DEFAULT_ICON = Shield

const providerLogoVariants = cva("w-4 h-4 inline mr-1")

/**
 * Returns the LucideIcon for the given OAuth provider
 * @param provider - The OAuth provider type
 * @returns The corresponding LucideIcon component, or default icon if not found
 */
export function getOAuthProviderIcon(
  provider: ClientOAuthProvider
): LucideIcon {
  return providerIcons[provider] || DEFAULT_ICON
}

export interface OAuthProviderLogoProps {
  className?: string
  provider: ClientOAuthProvider
  size?: number | string
}

/**
 * OAuthProviderLogo component - renders the icon for a given OAuth provider
 *
 * @example
 * ```tsx
 * <OAuthProviderLogo provider={ClientOAuthProvider.Google} />
 * <OAuthProviderLogo provider={ClientOAuthProvider.Google} className="h-6 w-6" />
 * ```
 */
const OAuthProviderLogo = memo(
  forwardRef<SVGSVGElement, OAuthProviderLogoProps>(
    ({ className, provider, size }, ref) => {
      const Icon = getOAuthProviderIcon(provider)
      return (
        <Icon
          ref={ref}
          className={cn(providerLogoVariants(), className)}
          size={size}
        />
      )
    }
  )
)

OAuthProviderLogo.displayName = "OAuthProviderLogo"

export { providerIcons }
export { OAuthProviderLogo }
export default OAuthProviderLogo
